/* eslint-disable */
// import React, { useEffect } from "react";
import { BehaviorSubject, Observable } from "rxjs";
import {
  IComponentContext,
  UpdateCommand,
  ComponentContextReadyState,
  DataflowIntegration,
  SingleValueParameter,
  MultiValueParameter,
} from "@sa/rep-core";
import { dataService } from "../../dataFlow";
import { OLAPResult } from "@sa/rep-olap";
import { SelectionMode, AggregationType } from "@sa/rep-common";
import { ComponentContextCreateOptionsBasf, ServicesRegister } from "../../shared";
import { debounceTime } from "rxjs/operators";
import { arrayByLength, clone, equal, uniq } from "../../../../shared";
import { SLIDE_NUMBER } from "./constants";
import { DictionariesService, DictionaryItem } from "@sa/rep-mdm";
import { getDefaultMark } from './utils/getDefaultMark';
import { getCurrentYear } from './utils/getCurrentYear';

const UNITS = [11];

interface Parameters {
  commodities: number;
  calendar: number[];
  sources: string[];
};
type DataType = "historical" | "forecast";

export class SlideContext implements IComponentContext {
  public id: string;
  public readonly options: ComponentContextCreateOptionsBasf;
  public readonly dataflowIntegration: DataflowIntegration;
  public readyState: ComponentContextReadyState;

  public olapResultHistorical$ = new BehaviorSubject<OLAPResult | null>(null);
  public olapResultForecast$ = new BehaviorSubject<OLAPResult | null>(null);
  public sourcesHistorical$ = new BehaviorSubject<string | null>(null);
  public sourcesForecast$ = new BehaviorSubject<string | null>(null);
  public loading$ = new BehaviorSubject<boolean>(true);

  protected _parameters: BehaviorSubject<Parameters | undefined> = new BehaviorSubject<Parameters | undefined>(undefined);
  public get parameters(): Observable<Parameters | undefined> {
    return this._parameters.asObservable();
  }

  constructor(id: string, createOptions: ComponentContextCreateOptionsBasf) {
    this.id = id;
    this.options = createOptions;

    this.dataflowIntegration = {
      canIntegrate: true, // Указываем, что компонент интегрируется к Dataflow сервису (и может управляться с помощью параметров отчёта)
      integrated: false,
    };

    this.readyState = ComponentContextReadyState.Loading;

    this._initDefaultMark();

    this._parameters
      .pipe(debounceTime(300))
      .subscribe(async (parameters) => {
        if (parameters) {
          this.readyState = ComponentContextReadyState.Loading;
          this.loading$.next(true);
          this.olapResultHistorical$.next(null);
          this.olapResultForecast$.next(null);

          const olapResultHistoricalPromise = this.query(parameters, "historical");
          const olapResultForecastPromise = this.query(parameters, "forecast");

          const [olapResultHistorical, olapResultForecast] = await Promise.all([olapResultHistoricalPromise, olapResultForecastPromise]);

          this.olapResultHistorical$.next(olapResultHistorical);
          this.olapResultForecast$.next(olapResultForecast);
          this.readyState = ComponentContextReadyState.Loaded;
          this.loading$.next(false);
        }
      });
  }

  private async _initDefaultMark() {
    const { dataflowService } = this.options;
    const commoditiesParam = dataflowService.getParameter("@commodities") as SingleValueParameter;
    const calendarParam = dataflowService.getParameter("@calendar") as MultiValueParameter;
    const sourcesParam = dataflowService.getParameter("@sources") as MultiValueParameter;
    const limitCalendarParam = dataflowService.getParameter("@limit_calendar") as SingleValueParameter;

    const { serviceProvider } = this.options;
    const dictionariesService = serviceProvider.resolve<DictionariesService>(ServicesRegister.Dictionary);
    const { calendar, limitDate } = await getDefaultMark(dictionariesService);
    calendarParam.value = calendar;
    limitCalendarParam.value = limitDate;

    this._parameters.next({
      commodities: commoditiesParam.value as number,
      calendar: calendarParam.value as number[],
      sources: sourcesParam.value as string[],
    });
  }

  async update(command: UpdateCommand): Promise<void> {
    const commodities = command.values.get("@commodities") as number;
    const sources = command.values.get("@sources") as string[];
    const parameters = clone(this._parameters.value);
    if (parameters !== undefined) {
      if (commodities !== undefined) {
        parameters.commodities = commodities;
      }

      if (sources?.length) {
        parameters.sources = sources;
      }

      if (!equal(parameters, this._parameters.value)) {
        this._parameters.next(parameters);
      }
    }
  }

  async query(parameters: Parameters, dataType: DataType) {
    const { commodities, sources } = parameters;

    const currentYear = getCurrentYear();
    const calendar = parameters.calendar.filter(e => dataType === "historical" ? (e <= currentYear) : (e >= currentYear));

    const { dataSourcesService } = this.options;

    const dataSourceHistoricalPrms = dataSourcesService.getDataSources(SLIDE_NUMBER, 1, commodities, item => {
      return item.getValue<string>("comment") === "historical";
    });
    const dataSourceForecastPrms = dataSourcesService.getDataSources(SLIDE_NUMBER, 1, commodities, item => {
      const isForecast = item.getValue<string>("comment") === "forecast";
      const id = item.getValue<string>("source");
      return isForecast && sources.includes(id || '');
    });

    const cubeSlideDef = await dataService.getCubeDefinition(`slide_31_${dataType}`);
    const cubeParameters = clone(cubeSlideDef.parameters);
    cubeParameters["p_calendar"]!["defaultValue"] = calendar.join(',');
    cubeParameters["p_commodity_id"]!["defaultValue"] = commodities;

    if (dataType === "forecast") {
      cubeParameters["p_sources"]!["defaultValue"] = sources.join(',');
    }

    const cubeSlide = await dataService.getCube({
      id: cubeSlideDef.id,
      parameters: cubeParameters,
    });

    const olapQuery = ({
      columns: {
        showEmpty: false,
        parts: [
          {
            path: "calendar.default",
            selection: calendar,
            selectionMode: SelectionMode.Include,
          },
        ],
      },
      rows: {
        showEmpty: false,
        parts: [
          { path: "commodities.default", selection: [commodities], selectionMode: SelectionMode.Include },
          { path: "units.default", selection: UNITS, selectionMode: SelectionMode.Include },
          dataType === "forecast" ? { path: "sources.default", selection: sources, selectionMode: SelectionMode.Include } : null,
          {
            path: "Measures",
            measures: [
              { id: "value", aggregationType: AggregationType.None },
            ],
          },
        ].filter(Boolean),
      },
      sourceResolveOptions: {
        sourceId: cubeSlide.id,
      },
    } as any);

    const olapResult = await cubeSlide.query(olapQuery);

    ["units"].filter(Boolean).map(dimension => {
      delete olapResult.data.aggregatedSelection[`${dimension}.default`];
      olapResult.data.rows.forEach(row => delete row.selection[`${dimension}.default`]);
    });

    const [dataSourceHistorical, dataSourceForecast] = await Promise.all([dataSourceHistoricalPrms, dataSourceForecastPrms]);
    this.sourcesHistorical$.next(dataSourceHistorical);
    this.sourcesForecast$.next(dataSourceForecast);

    return olapResult;
  }
}
